/**
 * Core domain types for Phase 1: markets, entities (LLCs), and properties.
 *
 * System of record for all three is Sixgen itself — see DATA_MODEL.md section 2.
 */
import { z } from 'zod';
/** Sixgen's markets. ARCHITECTURE.md section 2. */
export const MARKET_SLUGS = [
    'lexington-ky',
    'williamstown-ky',
    'shelbyville-simpsonville-ky',
    'cincinnati-area',
    'asheville-leicester-nc',
];
export const MARKET_NAMES = {
    'lexington-ky': 'Lexington, KY',
    'williamstown-ky': 'Williamstown, KY',
    'shelbyville-simpsonville-ky': 'Shelbyville / Simpsonville, KY',
    'cincinnati-area': 'Cincinnati Area',
    'asheville-leicester-nc': 'Asheville / Leicester, NC',
};
/**
 * Amenities that matter commercially for Sixgen's property profile — larger homes for families,
 * multigenerational groups, reunions, and destination travel. ARCHITECTURE.md section 2.
 */
export const AMENITIES = [
    'swimming_pool',
    'hot_tub',
    'sauna',
    'golf_simulator',
    'game_room',
    'theater',
    'putting_green',
    'outdoor_entertainment_area',
    'large_recreational_space',
];
export const amenitySchema = z.enum(AMENITIES);
export const AMENITY_LABELS = {
    swimming_pool: 'Swimming pool',
    hot_tub: 'Hot tub',
    sauna: 'Sauna',
    golf_simulator: 'Golf simulator',
    game_room: 'Game room',
    theater: 'Theater',
    putting_green: 'Putting green',
    outdoor_entertainment_area: 'Outdoor entertainment area',
    large_recreational_space: 'Large recreational space',
};
export const PROPERTY_STATUSES = ['active', 'onboarding', 'renovating', 'inactive'];
export const propertyStatusSchema = z.enum(PROPERTY_STATUSES);
/**
 * A legal entity (LLC) that owns or operates properties.
 *
 * Written in Phase 1 and unused until 2026-09-16, because nothing could create an entity —
 * `core.entity` had two write paths, the seed script and the eval fixtures (ROADMAP §7).
 *
 * `qbo_realm_id` is deliberately ABSENT. An entity is created with no company file, and the
 * QuickBooks connect flow binds one from what Intuit says was approved (`bindEntityRealm`). A
 * realm typed here would be an entity pointing at books nobody verified, and `bindEntityRealm`
 * would then refuse to connect it — which is exactly the wall hit on 2026-09-16, when every
 * seeded entity already carried a fictional realm and there was no free one to click.
 *
 * Trimmed, so that a name of spaces is refused rather than stored: `'  '` satisfies `min(1)` and
 * identifies nothing.
 */
export const newEntitySchema = z.object({
    name: z.string().trim().min(1).max(200),
    /** The name on the filing. An entity without one is not an entity. */
    legalName: z.string().trim().min(1).max(200),
    /** Where it is formed — `KY`, `DE`. */
    jurisdiction: z.string().trim().min(2).max(50),
    notes: z.string().max(2000).nullish(),
});
/**
 * A rename: the display name, the legal name, or both. Jurisdiction is not renamed — an entity
 * formed elsewhere is a different entity. Nothing else about an entity is editable here; the
 * company file and the Stripe account are bound by their own flows, from what the provider said.
 */
export const renameEntitySchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    legalName: z.string().trim().min(1).max(200).optional(),
})
    .refine((v) => v.name !== undefined || v.legalName !== undefined, {
    message: 'Give a name, a legal name, or both.',
});
/** Integer cents on the wire: a decimal string, never a JavaScript number (CLAUDE.md 3.1). */
const centsString = z.string().regex(/^\d+$/, 'integer cents as a decimal string');
const positiveCentsString = z.string().regex(/^[1-9]\d*$/, 'positive integer cents');
/**
 * A loan as the note states it (FINANCE.md §13, migration 0042). The rate is integer basis
 * points and every amount is integer cents as a string; the schedule is computed from these, not
 * entered. `paymentCents` absent means the annuity payment over the term.
 */
export const newLoanSchema = z.object({
    entityId: z.string().uuid(),
    propertyId: z.string().uuid().nullish(),
    lender: z.string().trim().min(1).max(200),
    name: z.string().trim().min(1).max(200),
    principalCents: positiveCentsString,
    /** 6.25% is 625. */
    annualRateBps: z.number().int().min(0).max(100_000),
    termMonths: z.number().int().min(1).max(600),
    paymentCents: positiveCentsString.nullish(),
    firstPaymentDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD'),
    rateType: z.enum(['fixed', 'variable']).default('fixed'),
    escrowCents: centsString.default('0'),
    notes: z.string().max(2000).nullish(),
});
/** Signed integer cents on the wire: a decimal string, never a JavaScript number. */
const signedCentsString = z.string().regex(/^-?\d+$/, 'signed integer cents as a decimal string');
/**
 * A month's budget for an entity, or one of its properties (FINANCE.md §10, migration 0046):
 * one signed amount per canonical line, in the statement engine's signs — revenue positive,
 * expense negative — so actual less budget is the variance and positive is favourable.
 */
export const budgetMonthSchema = z.object({
    entityId: z.string().uuid(),
    propertyId: z.string().uuid().nullish(),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM'),
    lines: z
        .array(z.object({
        canonicalKey: z.string().min(1).max(100),
        amountCents: signedCentsString,
        notes: z.string().max(2000).nullish(),
    }))
        .min(1)
        .max(200),
});
const isoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
/** A capital investment as the owner records it when it is planned (FINANCE.md §14, 0047). */
export const newCapexItemSchema = z.object({
    entityId: z.string().uuid(),
    propertyId: z.string().uuid().nullish(),
    name: z.string().trim().min(1).max(200),
    vendor: z.string().trim().max(200).nullish(),
    plannedOn: isoDateString,
    budgetCents: centsString,
    estimatedAnnualRevenueImpactCents: centsString.nullish(),
    notes: z.string().max(2000).nullish(),
});
/** Progressing an item: its status, what it actually cost, when it completed. */
export const updateCapexItemSchema = z
    .object({
    status: z.enum(['planned', 'in_progress', 'complete', 'cancelled']).optional(),
    actualCostCents: centsString.nullish(),
    completedOn: isoDateString.nullish(),
    notes: z.string().max(2000).nullish(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Nothing to update.',
});
/** An entity's cash floor (FINANCE.md §9.2, migration 0048): the minimum cash to keep, in cents. */
export const reservePolicySchema = z.object({
    entityId: z.string().uuid(),
    minimumCashCents: centsString,
    notes: z.string().max(2000).nullish(),
});
// ---------------------------------------------------------------------------------------------
// Strategic and episodic memory (ARCHITECTURE.md §6, migration 0052). Phase 6 groundwork.
// ---------------------------------------------------------------------------------------------
export const POLICY_CATEGORIES = [
    'goals',
    'acquisition',
    'reserves',
    'pricing',
    'operations',
    'guest_service',
    'finance',
    'other',
];
/** A policy as the owner writes it: the key is a slug, the body is the standing word itself. */
export const policySchema = z.object({
    key: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(\.[a-z0-9_]+)*$/, 'A slug: lowercase words joined by dots'),
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(20_000),
    category: z.enum(POLICY_CATEGORIES),
});
export const EPISODE_KINDS = [
    'outage',
    'legal_dispute',
    'significant_complaint',
    'major_repair',
    'acquisition_decision',
    'pricing_change',
    'policy_change',
    'other',
];
export const EPISODE_LINK_KINDS = [
    'transaction',
    'approval',
    'ticket',
    'reservation',
    'event',
    'run',
];
export const episodeLinkSchema = z.object({
    kind: z.enum(EPISODE_LINK_KINDS),
    id: z.string().uuid(),
});
/** An episode as a person or the CEO agent writes it; status and decision are the writer's. */
export const newEpisodeSchema = z.object({
    kind: z.enum(EPISODE_KINDS),
    title: z.string().min(1).max(200),
    summary: z.string().min(1).max(4000),
    occurredOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD'),
    endedOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
        .nullish(),
    entityId: z.string().uuid().nullish(),
    propertyId: z.string().uuid().nullish(),
    links: z.array(episodeLinkSchema).max(50).default([]),
});
export const episodeDecisionSchema = z.object({
    decision: z.enum(['confirmed', 'rejected']),
});
export const newMarketSchema = z.object({
    slug: z.string().min(1).max(80),
    name: z.string().min(1).max(200),
    state: z.string().length(2),
});
/**
 * Creating a property is the write path Phase 1 demonstrates end to end: a `core.property` row
 * with lineage, an `sys.audit_log` row, and an `sys.event` row on the Activity feed within 5s.
 */
export const newPropertySchema = z.object({
    name: z.string().min(1).max(200),
    marketId: z.string().uuid(),
    entityId: z.string().uuid(),
    addressLine1: z.string().min(1).max(200),
    addressLine2: z.string().max(200).nullish(),
    city: z.string().min(1).max(100),
    state: z.string().length(2),
    postalCode: z.string().min(3).max(20),
    bedrooms: z.number().int().min(0).max(100),
    bathrooms: z.number().min(0).max(100),
    sleeps: z.number().int().min(0).max(200),
    amenities: z.array(amenitySchema).default([]),
    status: propertyStatusSchema.default('active'),
    notes: z.string().max(5000).nullish(),
});
export const updatePropertySchema = newPropertySchema.partial();
/** Roles. SECURITY.md section 2. One user today; the model exists from day one. */
export const ROLES = ['owner', 'admin', 'staff', 'viewer'];
export const roleSchema = z.enum(ROLES);
/** Kill switch scopes. SECURITY.md section 6. */
export const KILL_SWITCH_SCOPES = [
    'platform_read_only',
    'all_agents',
    'tool_execution',
    'autonomous_actions',
];
export const killSwitchScopeSchema = z.enum(KILL_SWITCH_SCOPES);
export const KILL_SWITCH_LABELS = {
    platform_read_only: 'Return the entire platform to READ-ONLY mode',
    all_agents: 'Disable all agents',
    tool_execution: 'Disable tool execution',
    autonomous_actions: 'Disable autonomous actions',
};
// ---------------------------------------------------------------------------------------------
// Approvals. ARCHITECTURE.md 8, SECURITY.md 4. Phase 2.
// ---------------------------------------------------------------------------------------------
/**
 * The four things an owner can do with an approval request: APPROVE, REJECT, MODIFY, COMMENT.
 *
 * MODIFY is not a separate decision — it is an approval whose input differs from the one the
 * agent proposed, which is why `finalInput` lives here rather than in a second endpoint. An owner
 * who has to reject-and-ask-again to change one number will stop reading the details.
 */
export const approvalDecisionSchema = z
    .object({
    decision: z.enum(['approve', 'reject']),
    /** Present only when the owner changed the action. Absent means "as proposed". */
    finalInput: z.unknown().optional(),
    comment: z.string().max(4000).optional(),
})
    .refine((d) => d.decision === 'approve' || d.finalInput === undefined, {
    message: 'A rejected action has no input to execute. Drop finalInput or approve it.',
    path: ['finalInput'],
});
/**
 * Many approvals, one decision. No `finalInput`: MODIFY is a judgement about one action, and an
 * owner approving fifty proposals at once is not re-reading fifty inputs. The bound is the API's
 * own: a hundred sequential decisions is a long request already, and the screen pages before that.
 * Duplicates are accepted and collapsed by the route — a repeated id yields one result.
 */
export const approvalBulkDecisionSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(100),
    decision: z.enum(['approve', 'reject']),
    comment: z.string().max(4000).optional(),
});
/**
 * `POST /api/bookkeeping/rule-hits/approve`. Up to 200 transactions per request — a screen of
 * rule hits is one click (FINANCE.md 2). Duplicates are accepted and collapsed by the route: a
 * repeated id yields one result row, as the bulk decision route does.
 */
export const approveRuleHitsSchema = z.object({
    transactionIds: z.array(z.string().uuid()).min(1).max(200),
});
/**
 * The status of an approval REQUEST. Distinct from `lineage.ts`'s APPROVAL_STATUSES, which
 * describes whether a stored value was approved and includes `not_required`. A request is always
 * required — that is why it exists — so it has no such state.
 */
export const APPROVAL_REQUEST_STATUSES = ['pending', 'approved', 'rejected'];
export const approvalRequestStatusSchema = z.enum(APPROVAL_REQUEST_STATUSES);
// ---------------------------------------------------------------------------------------------
// Operations and maintenance (migration 0057, ROADMAP Phase 7 groundwork, AGENTS.md §5–6).
// ---------------------------------------------------------------------------------------------
export const TICKET_CATEGORIES = [
    'plumbing',
    'hvac',
    'electrical',
    'appliance',
    'structural',
    'pool_spa',
    'internet',
    'cleaning',
    'pest',
    'safety',
    'other',
];
export const TICKET_SEVERITIES = ['critical', 'high', 'medium', 'low'];
export const TICKET_STATUSES = [
    'open',
    'in_progress',
    'waiting_vendor',
    'resolved',
    'cancelled',
];
const isoDateTimeString = z.string().datetime({ offset: true });
/** A ticket as a person reports it. Severity is theirs to set or leave for the agent to assess. */
export const newTicketSchema = z.object({
    propertyId: z.string().uuid(),
    reservationId: z.string().uuid().nullish(),
    vendorId: z.string().uuid().nullish(),
    title: z.string().trim().min(1).max(200),
    description: z.string().max(4000).default(''),
    category: z.enum(TICKET_CATEGORIES),
    severity: z.enum(TICKET_SEVERITIES).nullish(),
    dueAt: isoDateTimeString.nullish(),
    estimatedCostCents: centsString.nullish(),
});
/** Progressing a ticket: status, severity, vendor, cost, resolution. */
export const updateTicketSchema = z
    .object({
    status: z.enum(TICKET_STATUSES).optional(),
    severity: z.enum(TICKET_SEVERITIES).nullish(),
    vendorId: z.string().uuid().nullish(),
    dueAt: isoDateTimeString.nullish(),
    resolution: z.string().max(4000).nullish(),
    estimatedCostCents: centsString.nullish(),
    actualCostCents: centsString.nullish(),
})
    .refine((u) => Object.keys(u).length > 0, { message: 'Nothing to update' });
export const OPS_TASK_KINDS = ['turnover', 'inspection', 'supplies', 'other'];
export const newOpsTaskSchema = z.object({
    propertyId: z.string().uuid(),
    reservationId: z.string().uuid().nullish(),
    kind: z.enum(OPS_TASK_KINDS),
    title: z.string().trim().min(1).max(200),
    dueAt: isoDateTimeString,
    assignee: z.string().trim().max(200).nullish(),
    notes: z.string().max(2000).nullish(),
});
export const updateOpsTaskSchema = z
    .object({
    status: z.enum(['open', 'done', 'skipped']).optional(),
    assignee: z.string().trim().max(200).nullish(),
    dueAt: isoDateTimeString.optional(),
    notes: z.string().max(2000).nullish(),
})
    .refine((u) => Object.keys(u).length > 0, { message: 'Nothing to update' });
export const newPreventiveScheduleSchema = z.object({
    propertyId: z.string().uuid().nullish(),
    category: z.enum(TICKET_CATEGORIES),
    title: z.string().trim().min(1).max(200),
    intervalDays: z.number().int().positive().max(3660),
    lastDoneOn: isoDateString.nullish(),
    notes: z.string().max(2000).nullish(),
});
export const updatePreventiveScheduleSchema = z
    .object({
    lastDoneOn: isoDateString.nullish(),
    intervalDays: z.number().int().positive().max(3660).optional(),
    isActive: z.boolean().optional(),
    notes: z.string().max(2000).nullish(),
})
    .refine((u) => Object.keys(u).length > 0, { message: 'Nothing to update' });
//# sourceMappingURL=domain.js.map